// The forecast's visuals (2026-10-06, our own look rather than a speedometer and sliders):
// - rangeStrip: the likely 12-month change as a band on an axis centered on 0%, the forecast marked;
// - scoreMeter: a 0-100 score as a number over a slim track with "typical" (50) marked;
// - driverChart: what pushes the price-pressure score up or down. Each driver's bar is its contribution,
//   weight × (score − 50), drawn left (pushing prices down) or right (up) of a center line. The weights add up to 1,
//   so the contributions add up to the score − 50.

import { el } from "./dom.ts";
import { OUTLOOK, outlookOf } from "../shared/outlook.ts";

export interface Driver {
  label: string;
  score: number | null;
  hint?: string;
  weight: number;
}

/** Each driver's contribution to the score (weight × (score − 50), in points), biggest first, and their sum. */
export function driverContributions(inputs: readonly Driver[]) {
  const rows = inputs.map((d) => ({ ...d, contribution: d.score == null ? 0 : d.weight * (d.score - 50) }))
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  return { rows, total: rows.reduce((s, r) => s + r.contribution, 0) };
}

const signed = (v: number, digits = 1) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(digits)}`;
const hintEl = (hint?: string) => (hint ? el("span", { class: "hint", title: hint }, "i") : null);

/** A 0-100 score: the number, then a track with the score's marker and a tick at 50 ("typical"). */
/** The outlook label as a pill with its arrow ("Leaning up ↗"), colored up / down; nothing without a label. */
export function outlookPill(label: string | null, size?: "big"): HTMLElement | null {
  const o = outlookOf(label);
  if (!o) return null;
  return el("div", { class: ["pill", size, o === "unclear" ? null : o].filter(Boolean).join(" ") }, `${OUTLOOK[o].label} ${OUTLOOK[o].arrow}`);
}

export function scoreMeter(score: number | null, caption: string, hint?: string): HTMLElement {
  const v = score == null ? null : Math.min(Math.max(score, 0), 100);
  const marker = el("div", { class: "meter-marker" });
  if (v != null) marker.style.left = `${v}%`;
  else marker.hidden = true;
  return el("div", { class: "meter", role: "img", "aria-label": `${caption}: ${v == null ? "not available" : `${Math.round(v)} of 100`}` },
    el("div", { class: "meter-head" },
      el("span", { class: "meter-value" }, v == null ? "–" : String(Math.round(v))), el("span", { class: "meter-of" }, "/100"),
      el("span", { class: "meter-caption" }, caption, hintEl(hint))),
    el("div", { class: "meter-track" }, el("div", { class: "meter-mid" }), marker),
    el("div", { class: "meter-scale" }, el("span", {}, "0"), el("span", {}, "50 typical"), el("span", {}, "100")));
}

/** The likely range (lo … hi) on an axis centered on 0%, shaded, with the forecast as a dot. */
export function rangeStrip(lo: number, mid: number, hi: number): HTMLElement {
  // a symmetric axis, rounded up to 5 points, so 0% sits in the middle and ranges compare at a glance
  const ext = Math.max(0.05, Math.ceil(Math.max(Math.abs(lo), Math.abs(hi)) * 20) / 20);
  const x = (v: number) => `${((v + ext) / (2 * ext)) * 100}%`;
  const pct = (v: number) => `${signed(v * 100, 0)}%`;
  const band = el("div", { class: "range-band" });
  band.style.left = x(lo);
  band.style.width = `${((hi - lo) / (2 * ext)) * 100}%`;
  const dot = el("div", { class: "range-mid", title: `Middle estimate ${signed(mid * 100)}%` });
  dot.style.left = x(mid);
  return el("div", { class: "range-strip", role: "img", "aria-label": `Likely change over the next 12 months: ${pct(lo)} to ${pct(hi)}, middle ${signed(mid * 100)}%` },
    el("div", { class: "range-axis" }, el("div", { class: "range-zero" }), band, dot),
    el("div", { class: "range-scale" }, el("span", {}, pct(-ext)), el("span", {}, "0%"), el("span", {}, pct(ext))));
}

/**
 * What pushes the score: one row per driver, its bar = its contribution, left = pushing prices down, right = up,
 * on a scale set by the biggest one. Under the label: the driver's own score (vs. other areas) and its weight.
 */
export function driverChart(inputs: readonly Driver[], score: number | null): HTMLElement {
  const { rows, total } = driverContributions(inputs);
  const scale = Math.max(2, ...rows.map((r) => Math.abs(r.contribution)));
  return el("div", { class: "drivers" },
    el("div", { class: "drivers-head muted small" }, el("span", {}, "← pushing prices down"), el("span", {}, "pushing prices up →")),
    ...rows.map((r) => {
      const bar = el("div", { class: `driver-bar ${r.contribution >= 0 ? "up" : "down"}` });
      const w = (Math.abs(r.contribution) / scale) * 50;
      bar.style.width = `${w}%`;
      bar.style.left = r.contribution >= 0 ? "50%" : `${50 - w}%`;
      return el("div", { class: "driver" },
        el("div", { class: "driver-label" },
          el("span", { class: "driver-name" }, r.label, hintEl(r.hint)),
          el("span", { class: "driver-meta muted" }, r.score == null ? "no data" : `score ${Math.round(r.score)} · ${Math.round(r.weight * 100)}% weight`)),
        el("div", { class: "driver-track" }, el("div", { class: "driver-zero" }), bar),
        el("span", { class: `driver-pts ${r.contribution >= 0 ? "up" : "down"}` }, signed(r.contribution)));
    }),
    score == null ? null : el("p", { class: "drivers-sum muted small" },
      `Price pressure ${Math.round(score)} = 50 (typical) ${total >= 0 ? "+" : "−"} ${Math.abs(total).toFixed(1)} from these drivers.`));
}
